import React, { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { Info, DollarSign, TrendingUp } from 'lucide-react';
import { DownloadPdfButton } from '@/components/DownloadPdfButton';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n || 0);
const fmtFull = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const STREAM_COLORS = ['#1B2A4A', '#2D5F8A', '#3B8C6E'];

export const RetirementProjectionPage = () => {
  const [loading, setLoading] = useState(true);
  const [projection, setProjection] = useState(null);

  // User inputs — pre-filled from account
  const [salary, setSalary] = useState(85000);
  const [tspBalance, setTspBalance] = useState(0);
  const [contribRate, setContribRate] = useState(5);
  const [retireAge, setRetireAge] = useState(62);
  const [annualReturn, setAnnualReturn] = useState(6);
  const [yearsService, setYearsService] = useState(20);
  const [high3, setHigh3] = useState(85000);
  const [ssMonthly, setSsMonthly] = useState(1907);
  const [withdrawalRate, setWithdrawalRate] = useState(4);

  // Balances
  const [currentTrad, setCurrentTrad] = useState(0);
  const [currentRoth, setCurrentRoth] = useState(0);
  const [tradPct, setTradPct] = useState(0.7);
  const [currentAge, setCurrentAge] = useState(35);

  useEffect(() => { loadInitial(); }, []);

  const loadInitial = async () => {
    setLoading(true);
    try {
      const [balRes, contRes, summRes] = await Promise.allSettled([
        api.get('/tsp/participant/balance'),
        api.get('/tsp/participant/contributions'),
        api.get('/tsp/summary'),
      ]);
      if (balRes.status === 'fulfilled') {
        const b = balRes.value.data;
        setTspBalance(b.total || 0);
        setCurrentTrad(b.traditional || 0);
        setCurrentRoth(b.roth || 0);
      }
      if (contRes.status === 'fulfilled') {
        const c = contRes.value.data;
        setSalary(c.annual_salary || 85000);
        setHigh3(c.annual_salary || 85000);
        setContribRate(c.rate || 5);
        setTradPct((c.traditional_pct || 70) / 100);
      }
      if (summRes.status === 'fulfilled') {
        const s = summRes.value.data;
        setCurrentAge(s?.participant?.age || 35);
        const yrs = s?.vesting?.years_of_service || 5;
        setYearsService(Math.round(yrs + Math.max(62 - (s?.participant?.age || 35), 0)));
      }
    } catch {} finally { setLoading(false); }
  };

  // Client-side projection (mirrors backend formula for instant updates)
  const calc = useMemo(() => {
    const n = Math.max(retireAge - currentAge, 0);
    const r = annualReturn / 100;
    const empAnnual = salary * (contribRate / 100);
    const matchPct = Math.min(contribRate / 100, 0.03) * 1.0 + Math.min(Math.max(contribRate / 100 - 0.03, 0), 0.02) * 0.5;
    const agencyAnnual = salary * 0.01 + salary * matchPct;
    const annualContrib = empAnnual + agencyAnnual;
    const tradAnnual = empAnnual * tradPct + agencyAnnual;
    const rothAnnual = empAnnual * (1 - tradPct);

    let projTotal, projTrad, projRoth;
    if (r > 0 && n > 0) {
      const gf = (1 + r) ** n;
      const af = (gf - 1) / r;
      projTotal = tspBalance * gf + annualContrib * af;
      projTrad = currentTrad * gf + tradAnnual * af;
      projRoth = currentRoth * gf + rothAnnual * af;
    } else if (n > 0) {
      projTotal = tspBalance + annualContrib * n;
      projTrad = currentTrad + tradAnnual * n;
      projRoth = currentRoth + rothAnnual * n;
    } else {
      projTotal = tspBalance; projTrad = currentTrad; projRoth = currentRoth;
    }

    const wdRate = withdrawalRate / 100;
    const tspMo = projTotal * wdRate / 12;
    const multiplier = (retireAge >= 62 && yearsService >= 20) ? 0.011 : 0.01;
    const fersMo = high3 * multiplier * yearsService / 12;
    const totalMo = tspMo + fersMo + ssMonthly;

    return {
      projTotal, projTrad, projRoth,
      tspMo, fersMo, totalMo,
      n, annualContrib, empAnnual, agencyAnnual, multiplier,
    };
  }, [salary, tspBalance, contribRate, retireAge, annualReturn, yearsService, high3, ssMonthly, withdrawalRate, currentAge, currentTrad, currentRoth, tradPct]);

  if (loading) return <div className="panel p-6 animate-pulse"><div className="h-8 bg-gray-100 rounded w-64 mb-4" /><div className="h-60 bg-gray-50 rounded" /></div>;

  const incomeData = [
    { name: 'Retirement Drawdown', value: Math.round(calc.tspMo), color: STREAM_COLORS[0] },
    { name: 'FERS Pension', value: Math.round(calc.fersMo), color: STREAM_COLORS[1] },
    { name: 'Social Security', value: Math.round(ssMonthly), color: STREAM_COLORS[2] },
  ];

  const inputClass = "w-full h-10 px-3 bg-white border border-[#D1D5DB] rounded text-sm text-[#1B2A4A] font-mono focus:outline-none focus:border-[#1B2A4A] focus:ring-1 focus:ring-[#1B2A4A]/20";
  const labelClass = "block text-[10px] font-medium text-[#6B7280] uppercase tracking-wider mb-1";

  return (
    <div className="space-y-4" data-testid="retirement-projection-page">
      {/* Download action */}
      <div className="flex justify-end">
        <DownloadPdfButton
          path="/exports/retirement-projection.pdf"
          params={{
            target_retirement_age: retireAge,
            annual_return_pct: annualReturn,
            withdrawal_rate_pct: withdrawalRate,
            social_security_monthly: ssMonthly,
            high3_override: high3,
            years_service_override: yearsService,
          }}
          filename="UniFed_RetirementProjection.pdf"
          label="Download Projection PDF"
          testId="download-projection-btn"
        />
      </div>
      {/* Primary Display */}
      <div className="panel p-6 bg-[#1B2A4A] text-white" data-testid="primary-display">
        <span className="text-[10px] font-medium text-[#8B95A5] uppercase tracking-wider">Estimated Monthly Retirement Income — In Today's Dollars</span>
        <div className="flex items-end gap-3 mt-2">
          <span className="font-mono text-4xl lg:text-5xl font-bold tracking-tight" data-testid="total-monthly-income">{fmt(calc.totalMo)}</span>
          <span className="text-lg text-[#8B95A5] font-mono mb-1">/mo</span>
        </div>
        <div className="mt-4 grid grid-cols-3 gap-4">
          {incomeData.map(s => (
            <div key={s.name} className="flex items-center gap-2.5" data-testid={`stream-${s.name.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: s.color }} />
              <div>
                <span className="text-[10px] text-[#8B95A5] block">{s.name}</span>
                <span className="font-mono text-sm font-semibold text-white">{fmtFull(s.value)}</span>
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[10px] text-[#5C6A7A]">Retiring at age {retireAge} with {yearsService} years of service · {annualReturn}% avg return · {withdrawalRate}% withdrawal rate</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* User Inputs */}
        <div className="lg:col-span-1 space-y-4">
          <div className="panel p-5" data-testid="projection-inputs">
            <span className="overline mb-4 block">Your Assumptions</span>
            <div className="space-y-3">
              <div><label className={labelClass}>Current Base Salary</label><input type="number" value={salary} onChange={e => { setSalary(parseFloat(e.target.value) || 0); setHigh3(parseFloat(e.target.value) || 0); }} className={inputClass} data-testid="input-salary" /></div>
              <div><label className={labelClass}>Current Retirement Balance</label><input type="number" value={Math.round(tspBalance)} onChange={e => setTspBalance(parseFloat(e.target.value) || 0)} className={inputClass} data-testid="input-balance" /></div>
              <div><label className={labelClass}>Annual Contribution Rate %</label>
                <div className="flex items-center gap-2"><input type="range" min={0} max={25} step={0.5} value={contribRate} onChange={e => setContribRate(parseFloat(e.target.value))} className="flex-1" data-testid="input-rate-slider" /><span className="font-mono text-sm font-semibold text-[#1B2A4A] w-12 text-right">{contribRate}%</span></div>
              </div>
              <div><label className={labelClass}>Target Retirement Age</label><input type="number" min={50} max={75} value={retireAge} onChange={e => setRetireAge(parseInt(e.target.value) || 62)} className={inputClass} data-testid="input-retire-age" /></div>
              <div><label className={labelClass}>Avg Annual Return %</label>
                <div className="flex items-center gap-2"><input type="range" min={0} max={12} step={0.5} value={annualReturn} onChange={e => setAnnualReturn(parseFloat(e.target.value))} className="flex-1" data-testid="input-return-slider" /><span className="font-mono text-sm font-semibold text-[#1B2A4A] w-12 text-right">{annualReturn}%</span></div>
              </div>
              <div><label className={labelClass}>Years of Service at Retirement</label><input type="number" value={yearsService} onChange={e => setYearsService(parseInt(e.target.value) || 0)} className={inputClass} data-testid="input-years-service" /></div>
              <div><label className={labelClass}>High-3 Average Salary</label><input type="number" value={Math.round(high3)} onChange={e => setHigh3(parseFloat(e.target.value) || 0)} className={inputClass} data-testid="input-high3" /></div>
              <div><label className={labelClass}>Social Security Monthly (from SSA.gov)</label><input type="number" value={ssMonthly} onChange={e => setSsMonthly(parseFloat(e.target.value) || 0)} className={inputClass} data-testid="input-ss-monthly" /></div>
              <div><label className={labelClass}>Withdrawal Rate %</label>
                <div className="flex items-center gap-2"><input type="range" min={2} max={8} step={0.5} value={withdrawalRate} onChange={e => setWithdrawalRate(parseFloat(e.target.value))} className="flex-1" data-testid="input-withdrawal-slider" /><span className="font-mono text-sm font-semibold text-[#1B2A4A] w-12 text-right">{withdrawalRate}%</span></div>
              </div>
            </div>
          </div>
        </div>

        {/* Results */}
        <div className="lg:col-span-2 space-y-4">
          {/* Projected Retirement Balance */}
          <div className="panel p-5" data-testid="projected-balance-card">
            <span className="overline mb-3 block">Projected Retirement Balance at Retirement</span>
            <div className="font-mono text-3xl font-bold text-[#1B2A4A]" data-testid="projected-balance">{fmt(calc.projTotal)}</div>
            <p className="text-xs text-[#9CA3AF] mt-1">After {calc.n} years of growth at {annualReturn}% avg return</p>
            <div className="mt-4 grid grid-cols-2 gap-4">
              <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-3">
                <span className="text-[10px] text-[#9CA3AF] block">Traditional (Pre-Tax)</span>
                <span className="font-mono text-lg font-semibold text-[#1B2A4A]" data-testid="projected-traditional">{fmt(calc.projTrad)}</span>
              </div>
              <div className="bg-[#F8F9FA] border border-[#E2E5E9] rounded p-3">
                <span className="text-[10px] text-[#9CA3AF] block">Roth (Post-Tax)</span>
                <span className="font-mono text-lg font-semibold text-[#1B2A4A]" data-testid="projected-roth">{fmt(calc.projRoth)}</span>
              </div>
            </div>
          </div>

          {/* Income Breakdown */}
          <div className="panel p-5" data-testid="income-breakdown-card">
            <span className="overline mb-3 block">Monthly Income Streams</span>
            <div className="flex items-center gap-6">
              <div className="w-36 h-36 shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={incomeData} cx="50%" cy="50%" innerRadius={35} outerRadius={55} dataKey="value" stroke="#FFF" strokeWidth={2}>
                      {incomeData.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: '#FFF', border: '1px solid #E2E5E9', borderRadius: 4, fontFamily: 'IBM Plex Mono', fontSize: 12 }} formatter={v => [fmtFull(v), '']} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex-1 space-y-3">
                {incomeData.map(s => {
                  const pct = calc.totalMo > 0 ? (s.value / calc.totalMo * 100).toFixed(1) : 0;
                  return (
                    <div key={s.name}>
                      <div className="flex items-center justify-between mb-1">
                        <div className="flex items-center gap-2"><div className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: s.color }} /><span className="text-xs text-[#6B7280]">{s.name}</span></div>
                        <span className="font-mono text-sm font-semibold text-[#1B2A4A]">{fmtFull(s.value)}</span>
                      </div>
                      <div className="w-full h-1.5 bg-[#F0F1F3] rounded-sm overflow-hidden">
                        <div className="h-full rounded-sm" style={{ width: `${pct}%`, backgroundColor: s.color }} />
                      </div>
                      <span className="text-[9px] font-mono text-[#9CA3AF]">{pct}% of total</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Calculation Details */}
          <div className="panel p-5" data-testid="calculation-details">
            <span className="overline mb-3 block">Calculation Details</span>
            <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
              <div className="flex justify-between py-1 border-b border-[#F0F1F3]"><span className="text-[#9CA3AF]">Current Age</span><span className="font-mono text-[#1B2A4A]">{currentAge}</span></div>
              <div className="flex justify-between py-1 border-b border-[#F0F1F3]"><span className="text-[#9CA3AF]">Years to Retirement</span><span className="font-mono text-[#1B2A4A]">{calc.n}</span></div>
              <div className="flex justify-between py-1 border-b border-[#F0F1F3]"><span className="text-[#9CA3AF]">Annual Employee Contrib.</span><span className="font-mono text-[#1B2A4A]">{fmtFull(calc.empAnnual)}</span></div>
              <div className="flex justify-between py-1 border-b border-[#F0F1F3]"><span className="text-[#9CA3AF]">Annual Agency Contrib.</span><span className="font-mono text-[#1B2A4A]">{fmtFull(calc.agencyAnnual)}</span></div>
              <div className="flex justify-between py-1 border-b border-[#F0F1F3]"><span className="text-[#9CA3AF]">Total Annual Contrib.</span><span className="font-mono font-medium text-[#1B2A4A]">{fmtFull(calc.annualContrib)}</span></div>
              <div className="flex justify-between py-1 border-b border-[#F0F1F3]"><span className="text-[#9CA3AF]">FERS Multiplier</span><span className="font-mono text-[#1B2A4A]">{(calc.multiplier * 100).toFixed(1)}%{calc.multiplier === 0.011 ? ' (62+ & 20yr bonus)' : ''}</span></div>
              <div className="flex justify-between py-1 border-b border-[#F0F1F3]"><span className="text-[#9CA3AF]">FERS Annual Pension</span><span className="font-mono text-[#1B2A4A]">{fmtFull(calc.fersMo * 12)}</span></div>
              <div className="flex justify-between py-1 border-b border-[#F0F1F3]"><span className="text-[#9CA3AF]">Withdrawal Rate</span><span className="font-mono text-[#1B2A4A]">{withdrawalRate}%</span></div>
            </div>
          </div>

          {/* Formula */}
          <div className="bg-blue-50 border border-blue-200 rounded px-4 py-3" data-testid="formula-note">
            <div className="flex items-start gap-2">
              <Info size={13} className="text-blue-600 mt-0.5 shrink-0" />
              <div className="text-[10px] text-blue-700 leading-relaxed">
                <p className="font-medium mb-1">Retirement Account Growth Formula</p>
                <p className="font-mono">FV = (Balance × (1+r)^n) + (AnnualContrib × ((1+r)^n - 1) / r)</p>
                <p className="mt-1">r = {annualReturn}% annual return · n = {calc.n} years · Monthly drawdown = FV × {withdrawalRate}% / 12</p>
                <p className="mt-1">FERS Pension: {(calc.multiplier * 100).toFixed(1)}% × High-3 ({fmtFull(high3)}) × {yearsService} years = {fmtFull(calc.fersMo * 12)}/year</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="text-[10px] text-[#9CA3AF] text-center leading-relaxed">
        Projections are estimates and do not guarantee future results. FERS Pension: 5 U.S.C. § 8415 · Social Security estimates from SSA.gov · Past performance does not predict future returns.
      </div>
    </div>
  );
};
